import React, { useState, memo, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Crown, Coins, Users, Gift, Star, Shield, Trophy, Sparkles, CheckCircle2, Lock, Unlock, MessageCircle, Mail, Copy, ChevronRight, MapPin, Gem, Award, TrendingUp, Calendar, Zap, Bell, Target, X, Check } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { ScrollProgress } from '../components/ScrollProgress';
import { LanguageProvider } from '../context/LanguageContext';
// --- Types ---
interface Reward {
  id: string;
  title: string;
  cost: number;
  unlocked: boolean;
  icon: React.ReactNode;
  description: string;
}
// --- Redemption Modal ---
function RedemptionModal({
  reward,
  onClose,
  onRedeem
}: {
  reward: Reward;
  onClose: () => void;
  onRedeem: (reward: Reward) => void;
}) {
  const [redeeming, setRedeeming] = useState(false);
  const [redeemed, setRedeemed] = useState(false);
  const handleRedeem = () => {
    setRedeeming(true);
    setTimeout(() => {
      setRedeeming(false);
      setRedeemed(true);
      setTimeout(() => {
        onRedeem(reward);
        onClose();
      }, 1500);
    }, 1500);
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-4344">
      <motion.div initial={{
      opacity: 0,
      scale: 0.9,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.9,
      y: 20
    }} onClick={e => e.stopPropagation()} className="bg-[#0f0f23] border border-[#d4a017]/30 max-w-md w-full p-8 relative" data-id="element-4345">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#f5f0e8]/40 hover:text-[#f5f0e8] transition-colors" data-id="element-4346">
          <X className="w-5 h-5" data-id="element-4347" />
        </button>

        {!redeemed ? <>
            <div className="w-20 h-20 mx-auto rounded-full border border-[#d4a017]/40 bg-[#d4a017]/10 flex items-center justify-center mb-6 text-[#d4a017]" data-id="element-4348">
              {reward.icon}
            </div>

            <h3 className="font-serif text-3xl text-[#f5f0e8] text-center mb-4" data-id="element-4349">
              {reward.title}
            </h3>

            <p className="text-[#f5f0e8]/60 text-center mb-8 font-light" data-id="element-4350">
              {reward.description}
            </p>

            <div className="bg-[#0a0a0f] border border-[#f5f0e8]/10 p-6 mb-8" data-id="element-4351">
              <div className="flex items-center justify-between mb-4" data-id="element-4352">
                <span className="text-[#f5f0e8]/60 text-sm" data-id="element-4353">Cost</span>
                <div className="flex items-center gap-2 text-[#d4a017]" data-id="element-4354">
                  <span className="font-serif text-2xl" data-id="element-4355">{reward.cost}</span>
                  <Coins className="w-5 h-5" data-id="element-4356" />
                </div>
              </div>
              <div className="flex items-center justify-between" data-id="element-4357">
                <span className="text-[#f5f0e8]/60 text-sm" data-id="element-4358">Your Balance</span>
                <div className="flex items-center gap-2 text-[#f5f0e8]" data-id="element-4359">
                  <span className="font-serif text-2xl" data-id="element-4360">275</span>
                  <Coins className="w-5 h-5" data-id="element-4361" />
                </div>
              </div>
            </div>

            <button onClick={handleRedeem} disabled={redeeming} className="w-full bg-[#d4a017] hover:bg-[#b8890f] text-[#0a0a0f] py-4 text-sm tracking-[0.2em] uppercase font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3" data-id="element-4362">
              {redeeming ? <>
                  <div className="w-4 h-4 border-2 border-[#0a0a0f]/30 border-t-[#0a0a0f] rounded-full animate-spin" data-id="element-4363"></div>
                  Redeeming...
                </> : <>
                  <Gift className="w-4 h-4" data-id="element-4364" />
                  Redeem Now
                </>}
            </button>
          </> : <div className="text-center py-8" data-id="element-4365">
            <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} className="w-20 h-20 mx-auto rounded-full bg-[#10b981]/20 border border-[#10b981]/40 flex items-center justify-center mb-6" data-id="element-4366">
              <Check className="w-10 h-10 text-[#10b981]" data-id="element-4367" />
            </motion.div>
            <h3 className="font-serif text-3xl text-[#f5f0e8] mb-4" data-id="element-4368">
              Redeemed Successfully!
            </h3>
            <p className="text-[#f5f0e8]/60 font-light" data-id="element-4369">
              Your {reward.title} will be available at the event.
            </p>
          </div>}
      </motion.div>
    </motion.div>;
}
// --- Invite Modal ---
function InviteModal({
  onClose
}: {
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const inviteLink = 'kingdoms.com/invite/court-275';
  const handleCopy = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const handleWhatsApp = () => {
    const message = encodeURIComponent(`Join me in the Kingdom Circle! Invite others, earn Kingdom Coins, and unlock exclusive rewards for Life in the Sikh Kingdoms. ${inviteLink}`);
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };
  const handleEmail = () => {
    const subject = encodeURIComponent('Join the Kingdom Circle');
    const body = encodeURIComponent(`I'm inviting you to join the Kingdom Circle!\n\nInvite others, earn Kingdom Coins, and unlock exclusive rewards for Life in the Sikh Kingdoms.\n\nJoin here: ${inviteLink}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-4370">
      <motion.div initial={{
      opacity: 0,
      scale: 0.9,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.9,
      y: 20
    }} onClick={e => e.stopPropagation()} className="bg-[#0f0f23] border border-[#d4a017]/30 max-w-lg w-full p-8 relative" data-id="element-4371">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#f5f0e8]/40 hover:text-[#f5f0e8] transition-colors" data-id="element-4372">
          <X className="w-5 h-5" data-id="element-4373" />
        </button>

        <div className="w-20 h-20 mx-auto rounded-full border border-[#d4a017]/40 bg-[#d4a017]/10 flex items-center justify-center mb-6" data-id="element-4374">
          <Users className="w-10 h-10 text-[#d4a017]" data-id="element-4375" />
        </div>

        <h3 className="font-serif text-3xl text-[#f5f0e8] text-center mb-4" data-id="element-4376">
          Invite Friends
        </h3>

        <p className="text-[#f5f0e8]/60 text-center mb-8 font-light" data-id="element-4377">
          Share your unique link and earn Kingdom Coins when friends join.
        </p>

        <div className="mb-6" data-id="element-4378">
          <p className="text-[#f5f0e8]/40 text-xs tracking-widest uppercase mb-3" data-id="element-4379">
            Your Personal Link
          </p>
          <div className="flex items-center bg-[#0a0a0f] border border-[#f5f0e8]/10 p-4" data-id="element-4380">
            <span className="text-[#f5f0e8] font-light flex-1 truncate text-sm" data-id="element-4381">
              {inviteLink}
            </span>
            <button onClick={handleCopy} className="text-[#d4a017] hover:text-[#f5f0e8] transition-colors flex items-center gap-2 text-xs uppercase tracking-wider ml-4" data-id="element-4382">
              {copied ? <CheckCircle2 className="w-4 h-4" data-id="element-4383" /> : <Copy className="w-4 h-4" data-id="element-4384" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>

        <div className="space-y-3" data-id="element-4385">
          <button onClick={handleWhatsApp} className="w-full bg-[#10b981] hover:bg-[#059669] text-white px-8 py-4 text-sm tracking-wider uppercase font-medium transition-colors flex items-center justify-center gap-3" data-id="element-4386">
            <MessageCircle className="w-5 h-5" data-id="element-4387" />
            Share on WhatsApp
          </button>
          <button onClick={handleEmail} className="w-full border border-[#f5f0e8]/20 hover:border-[#f5f0e8]/40 text-[#f5f0e8] px-8 py-4 text-sm tracking-wider uppercase font-light transition-colors flex items-center justify-center gap-3 bg-[#f5f0e8]/5" data-id="element-4388">
            <Mail className="w-4 h-4" data-id="element-4389" />
            Email Invite
          </button>
        </div>
      </motion.div>
    </motion.div>;
}
// --- Hero Section ---
function HeroSection({
  onInviteClick
}: {
  onInviteClick: () => void;
}) {
  const scrollToRewards = () => {
    document.getElementById('rewards')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <section className="relative w-full min-h-screen bg-[#0a0a0f] flex items-center justify-center overflow-hidden pt-20 pb-32" data-id="element-4390">
      <div className="absolute inset-0" data-id="element-4391">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] bg-[#d4a017]/8 rounded-full blur-[150px]" data-id="element-4392"></div>
        <div className="absolute inset-0 bg-[url('https://uploadthingy.s3.us-west-1.amazonaws.com/pxKq2k9j3qcBE1TheAWARz/Experience_image.png')] opacity-5 bg-cover bg-center mix-blend-overlay" data-id="element-4393"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0f] via-transparent to-[#0a0a0f]" data-id="element-4394"></div>
      </div>

      <div className="relative z-10 text-center px-4 max-w-6xl mx-auto w-full" data-id="element-4395">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} data-id="element-4396">
          <Link to="/" className="inline-flex items-center gap-2 text-[#d4a017]/60 hover:text-[#d4a017] text-xs tracking-[0.3em] uppercase font-light mb-16 transition-colors" data-id="element-4397">
            <ArrowLeft className="w-3 h-3" data-id="element-4398" />
            Return to the Kingdom
          </Link>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} className="mb-16" data-id="element-4399">
          <div className="w-24 h-24 mx-auto border-2 border-[#d4a017]/20 rounded-full flex items-center justify-center mb-10 bg-gradient-to-br from-[#d4a017]/10 to-transparent backdrop-blur-sm shadow-[0_0_40px_rgba(212,160,23,0.15)]" data-id="element-4400">
            <Crown className="w-12 h-12 text-[#d4a017]" data-id="element-4401" />
          </div>
          <h1 className="font-serif text-6xl md:text-8xl text-[#f5f0e8] mb-8 tracking-tight leading-none" data-id="element-4402">
            Kingdom <span className="text-[#d4a017] italic" data-id="element-4403">Circle</span>
          </h1>
          <p className="text-xl md:text-2xl text-[#f5f0e8]/60 font-light mb-12 max-w-3xl mx-auto leading-relaxed" data-id="element-4404">
            Invite others. Earn Kingdom Coins. Unlock Treasures. Rise through
            the Court.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-20" data-id="element-4405">
            <button onClick={onInviteClick} className="w-full sm:w-auto bg-[#d4a017] hover:bg-[#b8890f] text-[#0a0a0f] px-10 py-5 text-xs tracking-[0.25em] uppercase font-semibold transition-all duration-300 flex items-center justify-center gap-3 shadow-[0_0_30px_rgba(212,160,23,0.3)]" data-id="element-4406">
              <Users className="w-4 h-4" data-id="element-4407" />
              Invite Friends
            </button>
            <button onClick={scrollToRewards} className="w-full sm:w-auto border border-[#d4a017]/30 hover:border-[#d4a017]/60 text-[#f5f0e8] px-10 py-5 text-xs tracking-[0.25em] uppercase font-light transition-all duration-300 flex items-center justify-center gap-3 bg-[#f5f0e8]/5 backdrop-blur-sm" data-id="element-4408">
              <Trophy className="w-4 h-4" data-id="element-4409" />
              View My Rewards
            </button>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 40
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.4
      }} className="max-w-4xl mx-auto" data-id="element-4410">
          <div className="border border-[#d4a017]/30 bg-gradient-to-br from-[#0f0f23]/95 to-[#1a1a2e]/95 backdrop-blur-xl p-10 md:p-12 relative overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.5)]" data-id="element-4411">
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#d4a017]/5 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/3" data-id="element-4412"></div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 relative z-10" data-id="element-4413">
              <div className="text-center" data-id="element-4414">
                <div className="flex items-center justify-center gap-2 mb-3" data-id="element-4415">
                  <Shield className="w-5 h-5 text-[#d4a017]/60" data-id="element-4416" />
                  <p className="text-[#f5f0e8]/40 text-[10px] tracking-[0.3em] uppercase" data-id="element-4417">
                    Current Rank
                  </p>
                </div>
                <p className="font-serif text-2xl text-[#d4a017]" data-id="element-4418">
                  Court Circle
                </p>
              </div>

              <div className="text-center" data-id="element-4419">
                <div className="flex items-center justify-center gap-2 mb-3" data-id="element-4420">
                  <Coins className="w-5 h-5 text-[#d4a017]/60" data-id="element-4421" />
                  <p className="text-[#f5f0e8]/40 text-[10px] tracking-[0.3em] uppercase" data-id="element-4422">
                    Coin Balance
                  </p>
                </div>
                <p className="font-serif text-2xl text-[#f5f0e8]" data-id="element-4423">275</p>
              </div>

              <div className="text-center" data-id="element-4424">
                <div className="flex items-center justify-center gap-2 mb-3" data-id="element-4425">
                  <Users className="w-5 h-5 text-[#d4a017]/60" data-id="element-4426" />
                  <p className="text-[#f5f0e8]/40 text-[10px] tracking-[0.3em] uppercase" data-id="element-4427">
                    Referrals
                  </p>
                </div>
                <p className="font-serif text-2xl text-[#f5f0e8]" data-id="element-4428">7</p>
              </div>

              <div className="text-center" data-id="element-4429">
                <div className="flex items-center justify-center gap-2 mb-3" data-id="element-4430">
                  <Target className="w-5 h-5 text-[#d4a017]/60" data-id="element-4431" />
                  <p className="text-[#f5f0e8]/40 text-[10px] tracking-[0.3em] uppercase" data-id="element-4432">
                    Next Unlock
                  </p>
                </div>
                <p className="font-serif text-lg text-[#10b981]" data-id="element-4433">
                  Darbar Circle
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}
// --- Status Snapshot ---
function StatusSnapshot() {
  const stats = [{
    label: 'Current Rank',
    value: 'Court Circle',
    icon: <Shield className="w-6 h-6" data-id="element-4434" />,
    color: 'text-[#d4a017]'
  }, {
    label: 'Kingdom Coins',
    value: '275',
    icon: <Coins className="w-6 h-6" data-id="element-4435" />,
    color: 'text-[#d4a017]'
  }, {
    label: 'Treasures Collected',
    value: '2 of 5',
    icon: <Gem className="w-6 h-6" data-id="element-4436" />,
    color: 'text-[#10b981]'
  }, {
    label: 'Successful Referrals',
    value: '7',
    icon: <Users className="w-6 h-6" data-id="element-4437" />,
    color: 'text-[#f5f0e8]'
  }, {
    label: 'Event Access',
    value: 'VIP Eligible',
    icon: <CheckCircle2 className="w-6 h-6" data-id="element-4438" />,
    color: 'text-[#10b981]'
  }, {
    label: 'Next Reward',
    value: '25 Coins',
    icon: <Gift className="w-6 h-6" data-id="element-4439" />,
    color: 'text-[#d4a017]'
  }];
  return <section className="py-20 bg-[#05050a] relative -mt-20 z-20" data-id="element-4440">
      <div className="container mx-auto px-4 max-w-7xl" data-id="element-4441">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-id="element-4442">
          {stats.map((stat, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="border border-[#f5f0e8]/10 bg-[#0f0f23]/80 backdrop-blur-sm p-6 text-center hover:border-[#d4a017]/30 transition-all duration-300 group cursor-pointer" data-id="element-4443">
              <div className={`${stat.color} mb-4 flex justify-center opacity-60 group-hover:opacity-100 transition-opacity`} data-id="element-4444">
                {stat.icon}
              </div>
              <p className="text-[#f5f0e8]/40 text-[9px] tracking-[0.3em] uppercase mb-2" data-id="element-4445">
                {stat.label}
              </p>
              <p className="font-serif text-xl text-[#f5f0e8]" data-id="element-4446">{stat.value}</p>
            </motion.div>)}
        </div>
      </div>
    </section>;
}
// --- Rank Progress ---
function RankProgress() {
  return <section className="py-24 bg-[#0a0a0f] border-y border-[#f5f0e8]/5" data-id="element-4447">
      <div className="container mx-auto px-4 max-w-5xl" data-id="element-4448">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="border border-[#d4a017]/20 bg-gradient-to-br from-[#0f0f23] to-[#1a1a2e] p-12 md:p-16 relative overflow-hidden" data-id="element-4449">
          <div className="absolute inset-0 bg-[url('https://uploadthingy.s3.us-west-1.amazonaws.com/pxKq2k9j3qcBE1TheAWARz/Experience_image.png')] opacity-5 bg-cover bg-center mix-blend-overlay" data-id="element-4450"></div>

          <div className="relative z-10" data-id="element-4451">
            <div className="flex items-center justify-between mb-12 flex-wrap gap-8" data-id="element-4452">
              <div className="flex items-center gap-4" data-id="element-4453">
                <div className="w-16 h-16 rounded-full border border-[#cd7f32]/40 bg-[#cd7f32]/10 flex items-center justify-center" data-id="element-4454">
                  <Shield className="w-8 h-8 text-[#cd7f32]" data-id="element-4455" />
                </div>
                <div data-id="element-4456">
                  <p className="text-[#f5f0e8]/40 text-xs tracking-widest uppercase mb-1" data-id="element-4457">
                    Current Rank
                  </p>
                  <p className="font-serif text-3xl text-[#f5f0e8]" data-id="element-4458">
                    Court Circle
                  </p>
                </div>
              </div>

              <ChevronRight className="w-8 h-8 text-[#d4a017]/40 hidden md:block" data-id="element-4459" />

              <div className="flex items-center gap-4" data-id="element-4460">
                <div data-id="element-4461">
                  <p className="text-[#f5f0e8]/40 text-xs tracking-widest uppercase mb-1 text-right" data-id="element-4462">
                    Next Rank
                  </p>
                  <p className="font-serif text-3xl text-[#d4a017]" data-id="element-4463">
                    Darbar Circle
                  </p>
                </div>
                <div className="w-16 h-16 rounded-full border border-[#d4a017]/40 bg-[#d4a017]/10 flex items-center justify-center" data-id="element-4464">
                  <Crown className="w-8 h-8 text-[#d4a017]" data-id="element-4465" />
                </div>
              </div>
            </div>

            <div className="mb-8" data-id="element-4466">
              <div className="flex justify-between text-sm mb-3" data-id="element-4467">
                <span className="text-[#f5f0e8]/60" data-id="element-4468">
                  Progress to Darbar Circle
                </span>
                <span className="text-[#d4a017] font-medium" data-id="element-4469">
                  7 / 10 referrals
                </span>
              </div>
              <div className="w-full h-2 bg-[#f5f0e8]/10 rounded-full overflow-hidden" data-id="element-4470">
                <motion.div initial={{
                width: 0
              }} whileInView={{
                width: '70%'
              }} viewport={{
                once: true
              }} transition={{
                duration: 1,
                delay: 0.3
              }} className="h-full bg-gradient-to-r from-[#cd7f32] via-[#d4a017] to-[#d4a017] relative shadow-[0_0_20px_rgba(212,160,23,0.4)]" data-id="element-4471">
                  <div className="absolute right-0 top-0 bottom-0 w-8 bg-white/20 blur-sm" data-id="element-4472"></div>
                </motion.div>
              </div>
            </div>

            <div className="bg-[#0a0a0f]/60 border border-[#d4a017]/20 p-6" data-id="element-4473">
              <p className="text-[#f5f0e8]/40 text-xs tracking-widest uppercase mb-3" data-id="element-4474">
                Unlock at Darbar Circle
              </p>
              <div className="flex items-start gap-3" data-id="element-4475">
                <Sparkles className="w-5 h-5 text-[#d4a017] mt-0.5 flex-shrink-0" data-id="element-4476" />
                <p className="text-[#f5f0e8] font-light leading-relaxed" data-id="element-4477">
                  Prototype invitation pool, premium check-in line, exclusive
                  treasure, and signed limited collectible.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}
// --- Invite Section ---
function InviteSection({
  onInviteClick
}: {
  onInviteClick: () => void;
}) {
  return <section className="py-24 bg-[#05050a]" data-id="element-4478">
      <div className="container mx-auto px-4 max-w-5xl" data-id="element-4479">
        <div className="text-center mb-16" data-id="element-4480">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-4481">
            <p className="text-[#d4a017]/60 text-xs tracking-[0.3em] uppercase mb-4" data-id="element-4482">
              Share the Kingdom
            </p>
            <h2 className="font-serif text-5xl text-[#f5f0e8] mb-6" data-id="element-4483">
              Carry the Kingdom Forward
            </h2>
            <p className="text-[#f5f0e8]/50 font-light text-lg max-w-2xl mx-auto" data-id="element-4484">
              The kingdom returns faster when more people join the circle.
            </p>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="border border-[#d4a017]/20 bg-[#0f0f23] p-10 md:p-12" data-id="element-4485">
          <button onClick={onInviteClick} className="w-full bg-[#d4a017] hover:bg-[#b8890f] text-[#0a0a0f] px-10 py-6 text-sm tracking-[0.25em] uppercase font-semibold transition-all duration-300 flex items-center justify-center gap-3 mb-10 shadow-[0_0_30px_rgba(212,160,23,0.2)]" data-id="element-4486">
            <Users className="w-5 h-5" data-id="element-4487" />
            Open Invite Panel
          </button>

          <div className="grid grid-cols-3 gap-6 border-t border-[#f5f0e8]/10 pt-10" data-id="element-4488">
            <div className="text-center" data-id="element-4489">
              <p className="font-serif text-4xl text-[#f5f0e8] mb-2" data-id="element-4490">18</p>
              <p className="text-[#f5f0e8]/40 text-[10px] uppercase tracking-widest" data-id="element-4491">
                Invites Sent
              </p>
            </div>
            <div className="text-center border-x border-[#f5f0e8]/10" data-id="element-4492">
              <p className="font-serif text-4xl text-[#d4a017] mb-2" data-id="element-4493">7</p>
              <p className="text-[#f5f0e8]/40 text-[10px] uppercase tracking-widest" data-id="element-4494">
                Joined
              </p>
            </div>
            <div className="text-center" data-id="element-4495">
              <p className="font-serif text-4xl text-[#f5f0e8] mb-2" data-id="element-4496">175</p>
              <p className="text-[#f5f0e8]/40 text-[10px] uppercase tracking-widest" data-id="element-4497">
                Coins Earned
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}
// --- Rewards Section ---
function RewardsSection({
  onRewardClick
}: {
  onRewardClick: (reward: Reward) => void;
}) {
  const rewards: Reward[] = [{
    id: '1',
    title: 'Collectible Coin',
    cost: 50,
    unlocked: true,
    icon: <Coins className="w-7 h-7" data-id="element-4498" />,
    description: 'A commemorative Kingdom Circle coin to mark your participation in the movement.'
  }, {
    id: '2',
    title: 'Artifact Pack',
    cost: 100,
    unlocked: true,
    icon: <Gem className="w-7 h-7" data-id="element-4499" />,
    description: 'A curated collection of digital and physical artifacts from the Sikh Kingdoms.'
  }, {
    id: '3',
    title: 'Premium Drink',
    cost: 75,
    unlocked: true,
    icon: <Gift className="w-7 h-7" data-id="element-4500" />,
    description: 'Complimentary premium beverage at the experience, inspired by royal court traditions.'
  }, {
    id: '4',
    title: 'Priority Line',
    cost: 150,
    unlocked: true,
    icon: <Zap className="w-7 h-7" data-id="element-4501" />,
    description: 'Skip the general entry line with expedited VIP check-in access.'
  }, {
    id: '5',
    title: 'Upgraded Seat Artifact',
    cost: 200,
    unlocked: true,
    icon: <Star className="w-7 h-7" data-id="element-4502" />,
    description: 'Enhanced seating with an exclusive artifact placed at your seat.'
  }, {
    id: '6',
    title: 'VIP Access',
    cost: 250,
    unlocked: false,
    icon: <Crown className="w-7 h-7" data-id="element-4503" />,
    description: 'Full VIP experience including private lounge access and exclusive content.'
  }, {
    id: '7',
    title: 'Prototype Preview',
    cost: 300,
    unlocked: false,
    icon: <Sparkles className="w-7 h-7" data-id="element-4504" />,
    description: 'Invitation to exclusive prototype testing events before the official launch.'
  }];
  return <section id="rewards" className="py-24 bg-[#0a0a0f] border-t border-[#f5f0e8]/5" data-id="element-4505">
      <div className="container mx-auto px-4 max-w-6xl" data-id="element-4506">
        <div className="text-center mb-16" data-id="element-4507">
          <p className="text-[#d4a017]/60 text-xs tracking-[0.3em] uppercase mb-4" data-id="element-4508">
            Live Redemption
          </p>
          <h2 className="font-serif text-5xl text-[#f5f0e8] mb-6" data-id="element-4509">
            Redeem at the Experience
          </h2>
          <p className="text-[#f5f0e8]/50 font-light text-lg" data-id="element-4510">
            Your Kingdom Coins become real-world value.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-4511">
          {rewards.map((reward, i) => <motion.button key={reward.id} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.05
        }} onClick={() => reward.unlocked && onRewardClick(reward)} disabled={!reward.unlocked} className={`p-8 text-center border transition-all duration-300 w-full ${reward.unlocked ? 'border-[#d4a017]/30 bg-gradient-to-br from-[#d4a017]/5 to-transparent hover:border-[#d4a017]/50 cursor-pointer hover:scale-105' : 'border-[#f5f0e8]/10 bg-[#0f0f23] opacity-50 cursor-not-allowed'}`} data-id="element-4512">
              <div className={`w-16 h-16 mx-auto rounded-full border flex items-center justify-center mb-6 ${reward.unlocked ? 'border-[#d4a017]/40 bg-[#d4a017]/10 text-[#d4a017]' : 'border-[#f5f0e8]/20 bg-[#0a0a0f] text-[#f5f0e8]/40'}`} data-id="element-4513">
                {reward.unlocked ? reward.icon : <Lock className="w-6 h-6" data-id="element-4514" />}
              </div>
              <h4 className="font-serif text-lg text-[#f5f0e8] mb-4" data-id="element-4515">
                {reward.title}
              </h4>
              <div className={`inline-flex items-center gap-2 text-sm tracking-wider uppercase ${reward.unlocked ? 'text-[#d4a017]' : 'text-[#f5f0e8]/40'}`} data-id="element-4516">
                <span data-id="element-4517">{reward.cost}</span>
                <Coins className="w-3 h-3" data-id="element-4518" />
              </div>
            </motion.button>)}
        </div>
      </div>
    </section>;
}
// --- Treasures Section ---
function TreasuresSection() {
  return <section className="py-24 bg-[#05050a]" data-id="element-4519">
      <div className="container mx-auto px-4 max-w-6xl" data-id="element-4520">
        <div className="text-center mb-16" data-id="element-4521">
          <p className="text-[#d4a017]/60 text-xs tracking-[0.3em] uppercase mb-4" data-id="element-4522">
            Your Collection
          </p>
          <h2 className="font-serif text-5xl text-[#f5f0e8] mb-6" data-id="element-4523">
            Kingdom Treasures
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16" data-id="element-4524">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="border border-[#d4a017]/30 bg-gradient-to-br from-[#0f0f23] to-[#1a1a2e] p-10 hover:border-[#d4a017]/50 transition-all duration-300 cursor-pointer group" data-id="element-4525">
            <div className="flex items-center gap-4 mb-8" data-id="element-4526">
              <div className="w-20 h-20 rounded-full border border-[#d4a017]/40 bg-[#d4a017]/10 flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-4527">
                <Gem className="w-10 h-10 text-[#d4a017]" data-id="element-4528" />
              </div>
              <div data-id="element-4529">
                <div className="inline-block bg-[#d4a017]/10 text-[#d4a017] text-[9px] tracking-widest uppercase px-3 py-1 border border-[#d4a017]/20 mb-2" data-id="element-4530">
                  Rare Artifact
                </div>
                <h3 className="font-serif text-2xl text-[#f5f0e8]" data-id="element-4531">
                  Royal Seal of Lahore
                </h3>
              </div>
            </div>
            <p className="text-[#f5f0e8]/50 font-light leading-relaxed mb-6" data-id="element-4532">
              A symbol of authority from the Darbar. Carry this to unlock hidden
              narratives within the experience.
            </p>
            <div className="flex items-center gap-2 text-[#10b981] text-sm" data-id="element-4533">
              <CheckCircle2 className="w-4 h-4" data-id="element-4534" />
              <span className="uppercase tracking-wider" data-id="element-4535">Collected</span>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="border border-[#d4a017]/30 bg-gradient-to-br from-[#0f0f23] to-[#1a1a2e] p-10 hover:border-[#d4a017]/50 transition-all duration-300 cursor-pointer group" data-id="element-4536">
            <div className="flex items-center gap-4 mb-8" data-id="element-4537">
              <div className="w-20 h-20 rounded-full border border-[#10b981]/40 bg-[#10b981]/10 flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-4538">
                <Shield className="w-10 h-10 text-[#10b981]" data-id="element-4539" />
              </div>
              <div data-id="element-4540">
                <div className="inline-block bg-[#10b981]/10 text-[#10b981] text-[9px] tracking-widest uppercase px-3 py-1 border border-[#10b981]/20 mb-2" data-id="element-4541">
                  Uncommon Artifact
                </div>
                <h3 className="font-serif text-2xl text-[#f5f0e8]" data-id="element-4542">
                  Akali Warrior's Token
                </h3>
              </div>
            </div>
            <p className="text-[#f5f0e8]/50 font-light leading-relaxed mb-6" data-id="element-4543">
              Carried by the Akali Nihangs. A mark of courage and spiritual
              devotion in the face of adversity.
            </p>
            <div className="flex items-center gap-2 text-[#10b981] text-sm" data-id="element-4544">
              <CheckCircle2 className="w-4 h-4" data-id="element-4545" />
              <span className="uppercase tracking-wider" data-id="element-4546">Collected</span>
            </div>
          </motion.div>
        </div>

        <div className="max-w-3xl mx-auto text-center" data-id="element-4547">
          <div className="flex justify-center gap-8 mb-8" data-id="element-4548">
            <motion.div whileHover={{
            scale: 1.1
          }} className="w-20 h-20 rounded-full border-2 border-[#d4a017] bg-[#d4a017]/10 flex items-center justify-center shadow-[0_0_20px_rgba(212,160,23,0.2)] cursor-pointer" data-id="element-4549">
              <Gem className="w-8 h-8 text-[#d4a017]" data-id="element-4550" />
            </motion.div>
            <motion.div whileHover={{
            scale: 1.1
          }} className="w-20 h-20 rounded-full border-2 border-[#10b981] bg-[#10b981]/10 flex items-center justify-center shadow-[0_0_20px_rgba(16,185,129,0.2)] cursor-pointer" data-id="element-4551">
              <Shield className="w-8 h-8 text-[#10b981]" data-id="element-4552" />
            </motion.div>
            <div className="w-20 h-20 rounded-full border border-[#f5f0e8]/20 bg-[#0f0f23] flex items-center justify-center opacity-40" data-id="element-4553">
              <Lock className="w-6 h-6 text-[#f5f0e8]/40" data-id="element-4554" />
            </div>
            <div className="w-20 h-20 rounded-full border border-[#f5f0e8]/20 bg-[#0f0f23] flex items-center justify-center opacity-40" data-id="element-4555">
              <Lock className="w-6 h-6 text-[#f5f0e8]/40" data-id="element-4556" />
            </div>
            <div className="w-20 h-20 rounded-full border border-[#f5f0e8]/20 bg-[#0f0f23] flex items-center justify-center opacity-40" data-id="element-4557">
              <Lock className="w-6 h-6 text-[#f5f0e8]/40" data-id="element-4558" />
            </div>
          </div>

          <div className="mb-4" data-id="element-4559">
            <div className="flex justify-between text-sm mb-3 text-[#f5f0e8]/60" data-id="element-4560">
              <span data-id="element-4561">Collection Progress</span>
              <span className="text-[#d4a017]" data-id="element-4562">2 of 5</span>
            </div>
            <div className="w-full h-1.5 bg-[#f5f0e8]/10 rounded-full overflow-hidden" data-id="element-4563">
              <motion.div initial={{
              width: 0
            }} whileInView={{
              width: '40%'
            }} viewport={{
              once: true
            }} transition={{
              duration: 1,
              delay: 0.3
            }} className="h-full bg-gradient-to-r from-[#d4a017] to-[#10b981]" data-id="element-4564" />
            </div>
          </div>

          <p className="text-[#f5f0e8]/40 text-sm font-light" data-id="element-4565">
            Collect all 5 artifacts to unlock the final{' '}
            <span className="text-[#d4a017]" data-id="element-4566">Darbar Relic</span>
          </p>
        </div>
      </div>
    </section>;
}
// --- Court Roll ---
function CourtRoll() {
  const leaders = [{
    name: 'Rajveer S.',
    rank: 'Founding Circle',
    referrals: 24,
    badge: 'First Fifty'
  }, {
    name: 'Simran K.',
    rank: 'Darbar Circle',
    referrals: 18,
    badge: 'Court Restorer'
  }, {
    name: 'Harpreet G.',
    rank: 'Darbar Circle',
    referrals: 15,
    badge: 'Five Rivers Collector'
  }, {
    name: 'Amrit P.',
    rank: 'Court Circle',
    referrals: 12,
    badge: 'Prototype Witness'
  }, {
    name: 'Navdeep B.',
    rank: 'Court Circle',
    referrals: 10,
    badge: 'Founding Supporter'
  }];
  return <section className="py-24 bg-[#0a0a0f] border-y border-[#f5f0e8]/5" data-id="element-4567">
      <div className="container mx-auto px-4 max-w-4xl" data-id="element-4568">
        <div className="text-center mb-16" data-id="element-4569">
          <p className="text-[#d4a017]/60 text-xs tracking-[0.3em] uppercase mb-4" data-id="element-4570">
            The Founders
          </p>
          <h2 className="font-serif text-5xl text-[#f5f0e8] mb-6" data-id="element-4571">
            Court Roll
          </h2>
          <p className="text-[#f5f0e8]/50 font-light" data-id="element-4572">
            Those who carry the kingdom forward.
          </p>
        </div>

        <div className="space-y-4" data-id="element-4573">
          {leaders.map((leader, i) => <motion.div key={i} initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className={`border bg-[#0f0f23] p-6 flex items-center justify-between hover:border-[#d4a017]/40 transition-all duration-300 cursor-pointer ${i === 0 ? 'border-[#d4a017]/40 shadow-[0_0_30px_rgba(212,160,23,0.1)]' : 'border-[#f5f0e8]/10'}`} data-id="element-4574">
              <div className="flex items-center gap-6" data-id="element-4575">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center font-serif text-xl ${i === 0 ? 'bg-[#d4a017]/20 text-[#d4a017] border-2 border-[#d4a017]/40' : 'bg-[#f5f0e8]/5 text-[#f5f0e8]/60 border border-[#f5f0e8]/20'}`} data-id="element-4576">
                  {i + 1}
                </div>
                <div data-id="element-4577">
                  <p className="text-[#f5f0e8] font-medium mb-1" data-id="element-4578">
                    {leader.name}
                  </p>
                  <div className="flex items-center gap-3" data-id="element-4579">
                    <span className="text-[#f5f0e8]/40 text-xs tracking-wider uppercase" data-id="element-4580">
                      {leader.rank}
                    </span>
                    <span className="text-[#f5f0e8]/20" data-id="element-4581">•</span>
                    <span className="text-[#d4a017] text-xs tracking-wider uppercase" data-id="element-4582">
                      {leader.badge}
                    </span>
                  </div>
                </div>
              </div>
              <div className="text-right" data-id="element-4583">
                <p className="font-serif text-2xl text-[#f5f0e8] mb-1" data-id="element-4584">
                  {leader.referrals}
                </p>
                <p className="text-[#f5f0e8]/40 text-[10px] uppercase tracking-widest" data-id="element-4585">
                  Referrals
                </p>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}
// --- Honors Section ---
function HonorsSection() {
  const honors = [{
    title: 'First Fifty',
    desc: 'Among the first 50 to join',
    earned: true,
    icon: <Star className="w-6 h-6" data-id="element-4586" />
  }, {
    title: 'Five Rivers Collector',
    desc: 'Collected all 5 treasures',
    earned: false,
    icon: <Gem className="w-6 h-6" data-id="element-4587" />
  }, {
    title: 'Court Restorer',
    desc: '10+ successful referrals',
    earned: true,
    icon: <Users className="w-6 h-6" data-id="element-4588" />
  }, {
    title: 'Founding Supporter',
    desc: 'Reached Founding Circle',
    earned: false,
    icon: <Crown className="w-6 h-6" data-id="element-4589" />
  }, {
    title: 'Prototype Witness',
    desc: 'Attended prototype event',
    earned: false,
    icon: <Sparkles className="w-6 h-6" data-id="element-4590" />
  }];
  return <section className="py-24 bg-[#05050a]" data-id="element-4591">
      <div className="container mx-auto px-4 max-w-5xl" data-id="element-4592">
        <div className="text-center mb-16" data-id="element-4593">
          <p className="text-[#d4a017]/60 text-xs tracking-[0.3em] uppercase mb-4" data-id="element-4594">
            Recognition
          </p>
          <h2 className="font-serif text-5xl text-[#f5f0e8] mb-6" data-id="element-4595">
            Honors & Distinctions
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-4596">
          {honors.map((honor, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className={`p-8 border transition-all duration-300 cursor-pointer ${honor.earned ? 'border-[#d4a017]/30 bg-gradient-to-br from-[#d4a017]/5 to-transparent hover:border-[#d4a017]/50' : 'border-[#f5f0e8]/10 bg-[#0f0f23] opacity-60 hover:opacity-80'}`} data-id="element-4597">
              <div className={`w-14 h-14 rounded-full border flex items-center justify-center mb-6 ${honor.earned ? 'border-[#d4a017]/40 bg-[#d4a017]/10 text-[#d4a017]' : 'border-[#f5f0e8]/20 bg-[#0a0a0f] text-[#f5f0e8]/40'}`} data-id="element-4598">
                {honor.icon}
              </div>
              <h4 className="font-serif text-xl text-[#f5f0e8] mb-3" data-id="element-4599">
                {honor.title}
              </h4>
              <p className="text-[#f5f0e8]/50 text-sm font-light mb-4" data-id="element-4600">
                {honor.desc}
              </p>
              {honor.earned && <div className="flex items-center gap-2 text-[#10b981] text-xs uppercase tracking-wider" data-id="element-4601">
                  <CheckCircle2 className="w-4 h-4" data-id="element-4602" />
                  Earned
                </div>}
            </motion.div>)}
        </div>
      </div>
    </section>;
}
// --- Kingdom Updates ---
function KingdomUpdates() {
  const updates = [{
    type: 'Announcement',
    title: 'Prototype Event Confirmed',
    desc: 'March 2026 in Toronto',
    time: '2 days ago',
    icon: <Bell className="w-5 h-5" data-id="element-4603" />
  }, {
    type: 'Milestone',
    title: '500 Members Joined',
    desc: 'The circle grows stronger',
    time: '5 days ago',
    icon: <TrendingUp className="w-5 h-5" data-id="element-4604" />
  }, {
    type: 'Treasure Drop',
    title: 'New Artifact Unlocked',
    desc: "Maharaja's Seal now available",
    time: '1 week ago',
    icon: <Gem className="w-5 h-5" data-id="element-4605" />
  }, {
    type: 'Event',
    title: 'Trailer Release',
    desc: 'Official experience trailer live',
    time: '2 weeks ago',
    icon: <Calendar className="w-5 h-5" data-id="element-4606" />
  }];
  return <section className="py-24 bg-[#0a0a0f] border-t border-[#f5f0e8]/5" data-id="element-4607">
      <div className="container mx-auto px-4 max-w-4xl" data-id="element-4608">
        <div className="text-center mb-16" data-id="element-4609">
          <p className="text-[#d4a017]/60 text-xs tracking-[0.3em] uppercase mb-4" data-id="element-4610">
            Latest
          </p>
          <h2 className="font-serif text-5xl text-[#f5f0e8] mb-6" data-id="element-4611">
            From the Court
          </h2>
        </div>

        <div className="space-y-4" data-id="element-4612">
          {updates.map((update, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="border border-[#f5f0e8]/10 bg-[#0f0f23] p-6 hover:border-[#d4a017]/30 transition-colors cursor-pointer" data-id="element-4613">
              <div className="flex items-start gap-4" data-id="element-4614">
                <div className="w-12 h-12 rounded-full bg-[#d4a017]/10 border border-[#d4a017]/20 flex items-center justify-center text-[#d4a017] flex-shrink-0" data-id="element-4615">
                  {update.icon}
                </div>
                <div className="flex-1" data-id="element-4616">
                  <div className="flex items-center gap-3 mb-2" data-id="element-4617">
                    <span className="text-[#d4a017] text-xs tracking-wider uppercase" data-id="element-4618">
                      {update.type}
                    </span>
                    <span className="text-[#f5f0e8]/20" data-id="element-4619">•</span>
                    <span className="text-[#f5f0e8]/40 text-xs" data-id="element-4620">
                      {update.time}
                    </span>
                  </div>
                  <h4 className="text-[#f5f0e8] font-medium text-lg mb-1" data-id="element-4621">
                    {update.title}
                  </h4>
                  <p className="text-[#f5f0e8]/50 text-sm font-light" data-id="element-4622">
                    {update.desc}
                  </p>
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}
// --- Locations Section ---
function LocationsSection() {
  const locations = [{
    city: 'Toronto',
    members: 142
  }, {
    city: 'Brampton',
    members: 98
  }, {
    city: 'Mississauga',
    members: 76
  }, {
    city: 'Surrey',
    members: 64
  }, {
    city: 'Vancouver',
    members: 52
  }, {
    city: 'Vaughan',
    members: 38
  }, {
    city: 'London',
    members: 24
  }];
  return <section className="py-24 bg-[#05050a]" data-id="element-4623">
      <div className="container mx-auto px-4 max-w-5xl" data-id="element-4624">
        <div className="text-center mb-16" data-id="element-4625">
          <p className="text-[#d4a017]/60 text-xs tracking-[0.3em] uppercase mb-4" data-id="element-4626">
            The Circle Expands
          </p>
          <h2 className="font-serif text-5xl text-[#f5f0e8] mb-6" data-id="element-4627">
            Where the Kingdom Gathers
          </h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-4628">
          {locations.map((location, i) => <motion.div key={i} initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.05
        }} className="border border-[#f5f0e8]/10 bg-[#0f0f23] p-6 text-center hover:border-[#d4a017]/30 transition-all duration-300 cursor-pointer hover:scale-105" data-id="element-4629">
              <MapPin className="w-6 h-6 text-[#d4a017]/60 mx-auto mb-4" data-id="element-4630" />
              <p className="text-[#f5f0e8] font-medium mb-2" data-id="element-4631">{location.city}</p>
              <p className="text-[#f5f0e8]/40 text-sm" data-id="element-4632">
                {location.members} members
              </p>
            </motion.div>)}
        </div>
      </div>
    </section>;
}
// --- Rank System ---
function RankSystemSection() {
  const ranks = [{
    name: 'River Circle',
    threshold: '0–50 Coins',
    benefits: ['General early access', 'Digital treasure', 'Insider updates'],
    color: 'border-[#f5f0e8]/20',
    iconColor: 'text-[#f5f0e8]/60',
    icon: <Shield className="w-8 h-8" data-id="element-4633" />
  }, {
    name: 'Court Circle',
    threshold: '50–150 Coins',
    benefits: ['Priority waitlist', 'Collectible upgrade', 'Better seat zone', 'Early merch access'],
    color: 'border-[#cd7f32]/40',
    iconColor: 'text-[#cd7f32]',
    icon: <Award className="w-8 h-8" data-id="element-4634" />
  }, {
    name: 'Darbar Circle',
    threshold: '150–300 Coins',
    benefits: ['Prototype invitation', 'Premium check-in', 'Exclusive treasure', 'Limited collectible'],
    color: 'border-[#c2b280]/60',
    iconColor: 'text-[#c2b280]',
    icon: <Trophy className="w-8 h-8" data-id="element-4635" />
  }, {
    name: 'Founding Circle',
    threshold: '300+ Coins',
    benefits: ['VIP access', 'Hosted preview', 'Premium drink', 'Founder recognition', 'Private events'],
    color: 'border-[#d4a017]',
    iconColor: 'text-[#d4a017]',
    icon: <Crown className="w-8 h-8" data-id="element-4636" />,
    glow: true
  }];
  return <section className="py-24 bg-[#0a0a0f] border-y border-[#f5f0e8]/5" data-id="element-4637">
      <div className="container mx-auto px-4 max-w-6xl" data-id="element-4638">
        <div className="text-center mb-16" data-id="element-4639">
          <p className="text-[#d4a017]/60 text-xs tracking-[0.3em] uppercase mb-4" data-id="element-4640">
            Progression
          </p>
          <h2 className="font-serif text-5xl text-[#f5f0e8] mb-6" data-id="element-4641">
            The Court Ranks
          </h2>
          <p className="text-[#f5f0e8]/50 font-light text-lg max-w-2xl mx-auto" data-id="element-4642">
            Rise through the ranks. Unlock greater access, privileges, and
            recognition.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-4643">
          {ranks.map((rank, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className={`bg-[#0f0f23] border ${rank.color} p-8 relative hover:scale-105 transition-all duration-300 cursor-pointer ${rank.glow ? 'shadow-[0_0_40px_rgba(212,160,23,0.15)]' : ''}`} data-id="element-4644">
              {rank.glow && <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#d4a017] to-transparent" data-id="element-4645"></div>}
              <div className={`${rank.iconColor} mb-6`} data-id="element-4646">{rank.icon}</div>
              <h3 className="font-serif text-2xl text-[#f5f0e8] mb-2" data-id="element-4647">
                {rank.name}
              </h3>
              <p className="text-[#f5f0e8]/40 text-xs tracking-widest uppercase mb-8 pb-6 border-b border-[#f5f0e8]/10" data-id="element-4648">
                {rank.threshold}
              </p>

              <ul className="space-y-3" data-id="element-4649">
                {rank.benefits.map((benefit, j) => <li key={j} className="flex items-start gap-3 text-[#f5f0e8]/70 font-light text-sm" data-id="element-4650">
                    <CheckCircle2 className={`w-4 h-4 mt-0.5 flex-shrink-0 ${rank.iconColor}`} data-id="element-4651" />
                    {benefit}
                  </li>)}
              </ul>
            </motion.div>)}
        </div>
      </div>
    </section>;
}
// --- Final CTA ---
function FinalCTA({
  onInviteClick
}: {
  onInviteClick: () => void;
}) {
  const scrollToRewards = () => {
    document.getElementById('rewards')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <section className="py-32 bg-[#05050a] relative overflow-hidden border-t border-[#d4a017]/20" data-id="element-4652">
      <div className="absolute inset-0 bg-gradient-to-t from-[#d4a017]/5 to-transparent" data-id="element-4653"></div>
      <div className="absolute inset-0 bg-[url('https://uploadthingy.s3.us-west-1.amazonaws.com/pxKq2k9j3qcBE1TheAWARz/Experience_image.png')] opacity-5 bg-cover bg-center mix-blend-overlay" data-id="element-4654"></div>

      <div className="container mx-auto px-4 relative z-10 text-center max-w-4xl" data-id="element-4655">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} data-id="element-4656">
          <Crown className="w-16 h-16 text-[#d4a017] mx-auto mb-10" data-id="element-4657" />
          <h2 className="font-serif text-6xl md:text-7xl text-[#f5f0e8] mb-8 leading-tight" data-id="element-4658">
            Carry the Kingdom Forward
          </h2>
          <p className="text-2xl text-[#f5f0e8]/60 font-light mb-16 max-w-2xl mx-auto" data-id="element-4659">
            Every invitation brings the kingdom closer to life.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-4660">
            <button onClick={onInviteClick} className="w-full sm:w-auto bg-[#d4a017] hover:bg-[#b8890f] text-[#0a0a0f] px-12 py-6 text-sm tracking-[0.25em] uppercase font-semibold transition-all duration-300 shadow-[0_0_30px_rgba(212,160,23,0.3)]" data-id="element-4661">
              Invite More Friends
            </button>
            <button onClick={scrollToRewards} className="w-full sm:w-auto border border-[#f5f0e8]/20 hover:border-[#f5f0e8]/40 text-[#f5f0e8] px-12 py-6 text-sm tracking-[0.25em] uppercase font-light transition-all duration-300 bg-[#f5f0e8]/5 backdrop-blur-sm" data-id="element-4662">
              Unlock More Rewards
            </button>
            <Link to="/" className="w-full sm:w-auto border border-[#d4a017]/30 hover:border-[#d4a017]/60 text-[#d4a017] px-12 py-6 text-sm tracking-[0.25em] uppercase font-light transition-all duration-300 bg-[#d4a017]/5 backdrop-blur-sm inline-flex items-center justify-center" data-id="element-4663">
              Join the Next Experience
            </Link>
          </div>
        </motion.div>
      </div>
    </section>;
}
// --- Main Page Component ---
export function KingdomCirclePage() {
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedReward, setSelectedReward] = useState<Reward | null>(null);
  const handleRewardClick = (reward: Reward) => {
    setSelectedReward(reward);
  };
  const handleRewardRedeem = (reward: Reward) => {
    console.log('Redeemed:', reward);
    // Here you would typically make an API call to process the redemption
  };
  return <LanguageProvider data-id="element-4664">
      <div className="relative bg-[#0a0a0f] text-[#f5f0e8] overflow-x-hidden" data-id="element-4665">
        <ScrollProgress data-id="element-4666" />
        <Navbar data-id="element-4667" />

        <main className="w-full" data-id="element-4668">
          <HeroSection onInviteClick={() => setShowInviteModal(true)} data-id="element-4669" />
          <StatusSnapshot data-id="element-4670" />
          <RankProgress data-id="element-4671" />
          <InviteSection onInviteClick={() => setShowInviteModal(true)} data-id="element-4672" />
          <RewardsSection onRewardClick={handleRewardClick} data-id="element-4673" />
          <TreasuresSection data-id="element-4674" />
          <CourtRoll data-id="element-4675" />
          <HonorsSection data-id="element-4676" />
          <KingdomUpdates data-id="element-4677" />
          <LocationsSection data-id="element-4678" />
          <RankSystemSection data-id="element-4679" />
          <FinalCTA onInviteClick={() => setShowInviteModal(true)} data-id="element-4680" />
        </main>

        <Footer data-id="element-4681" />

        {/* Modals */}
        <AnimatePresence data-id="element-4682">
          {showInviteModal && <InviteModal onClose={() => setShowInviteModal(false)} data-id="element-4683" />}
          {selectedReward && <RedemptionModal reward={selectedReward} onClose={() => setSelectedReward(null)} onRedeem={handleRewardRedeem} data-id="element-4684" />}
        </AnimatePresence>
      </div>
    </LanguageProvider>;
}